import React, { useContext, useEffect } from 'react';
import AuthContext from '@cpg-its/ads.auth.auth-context';
import SessionTimeout from '@cpg-its/ads.auth.session-timeout';
import { useDispatch } from 'react-redux';
import { AuthSetting } from '../../global/constants/auth-settings';
import { performSiteUserLogout } from '../../global/helpers/site-user-logout';
import { CpgAppSelect } from '../../cic/cic-app-dropdown';
import { CicAppContext, CicAppKeys } from '../../cic/cic-nav-context';
import { Navigation } from '../../global/models/navigation-model';
import { getApplications, getRoles } from '../../global/ducks/wam-api';
import { dispatchSetApplicationList, setAccessToken, setRoleObject, setRoles } from '../../global/ducks/authenticated-slice';
import { AuthToken } from '../../global/models/authenticated-model';
import { ProfileKey } from '../../pages/arc/constants/constant-strings';
import { ExternalLinkModal } from '../../global/components/external-link-modal';
import { getTcsmEpiProps } from '../../global/ducks/epi-api';
import { getNotifications } from '../../global/ducks/notification-prc-api';
import { registerOdpLogin } from '../../global/helpers/odp-helper';
import { setNotifications } from '../../pages/notification/notification-slice';
import { NotificationBell } from './notification-bell';
import { isReloadPending, isServerSessionEstablished, reloadWithoutTrigger } from './prime-and-reload';
import { isServerSessionStale, reloadIfRestoredSignedIn, resetServerSessionSync, syncStaleServerSession } from './server-session-sync';
import { SignedInToast } from './signed-in-toast';


interface Props {
	navigation: Navigation,
	signInPath: string,
	signInText: string,
	siteSettingsId: string,
	currentCulture: string,
}

const onlyUnique = (value, index, self) => {
	return self.indexOf(value) === index;
};

const parseRoleNames = (data: any) => {
	if (!data || !data?.role_assignments) {
		return [];
	}
	const keys = Object.keys(data?.role_assignments);
	return Array.prototype.concat.apply([], keys?.map(key => data?.role_assignments[key].map(a => a?.role_name)));
};

export const CicNavWrapper = (props: Props) => {


	const { navigation, signInPath, signInText, siteSettingsId, currentCulture } = props;

	const { accessToken, decodedToken, logout, getAccessToken, tokenError } = useContext(AuthContext) as any;

	// Idle-timeout logout target — mirrors the cic-nav sign-out (returns to /services after the
	// hosted logout). SessionTimeout calls performSiteUserLogout instead of navigating here itself.
	const encodedReturnTo = encodeURIComponent(`${AuthSetting.REACT_APP_AUTH0_LOGOUT_URL}?returnTo=${window.location.origin}/services&client_id=${AuthSetting.REACT_APP_CLIENT_ID}`);
	const logoutUrl = `${AuthSetting.REACT_APP_CPG_LOGOUT_URL}?returnTo=${encodedReturnTo}`;

	const parsedGuid = (() => {
		if (decodedToken && decodedToken[ProfileKey]) {
			return (JSON.parse(decodedToken[ProfileKey]) as AuthToken)?.person?.guid;
		}
		return decodedToken;
	})();

	const dispatch = useDispatch();

	// Signed-out header over signed-in server content: make the server agree with the client.
	// See server-session-sync for why the client wins.
	useEffect(() => {
		if (isServerSessionStale(accessToken, tokenError)) {
			syncStaleServerSession();
		} else if (accessToken || !isServerSessionEstablished()) {
			resetServerSessionSync();
		}
	}, [accessToken, tokenError]);

	useEffect(() => {
		window.addEventListener('pageshow', reloadIfRestoredSignedIn);
		return () => window.removeEventListener('pageshow', reloadIfRestoredSignedIn);
	}, []);

	useEffect(() => {
		if (accessToken && decodedToken) {
			// Report the sign-in to ODP. This nav mounts on every CMS page, so the helper's
			// session marker — not this call site — is what keeps it to one event per login.
			registerOdpLogin(parsedGuid);

			// Auth bridge: hand the Auth0 token to the back-end so it can validate it and
			// issue a decoupled site-user cookie that server-rendered pages/blocks can read.
			// Non-fatal: the existing front-end UI works regardless of this call's outcome.
			fetch('/api/auth/site-session', {
				method: 'POST',
				headers: { Authorization: `Bearer ${accessToken}` },
				credentials: 'same-origin',
			})
				.then((res) => {
					// First-load fix: on the post-sign-in return (?src=si) where the server hadn't
					// established the session yet, once the cookie is primed reload once (trigger
					// stripped, other query values kept) so THIS page re-renders authenticated
					// instead of only on the next navigation. Skips the reload if the server
					// already rendered authenticated.
					if (res.ok && isReloadPending()) {
						reloadWithoutTrigger();
					}
				})
				.catch(() => { /* ignore: server cookie is best-effort */ });

			dispatch(setAccessToken(accessToken));
			dispatch(getRoles(
				parsedGuid,
				(res) => {
					const roles = parseRoleNames(res?.data)?.filter(onlyUnique);
					sessionStorage?.setItem('rolesMatrix', JSON.stringify(roles));
					dispatch(setRoles(roles));
					dispatch(setRoleObject(res?.data?.role_assignments || {}));
				},
			));
			dispatch(getApplications((res) => dispatchSetApplicationList(res, dispatch)));
			dispatch(getTcsmEpiProps(dispatch, siteSettingsId, { parsedGuid }, currentCulture, true));
			dispatch(getNotifications(
				parsedGuid,
				(res) => {
					dispatch(setNotifications(res?.data || []));
				},
			));
			const signinEmail = document.getElementById('cpg-login-email');
			const signinEmailButton = document.getElementById('cpg-login-email-button');
			signinEmail?.setAttribute('disabled', 'true');
			signinEmailButton?.setAttribute('disabled', 'true');
			signinEmail?.setAttribute('placeholder', navigation?.LoggedInText || 'You are already signed in');
		}
	}, [accessToken, decodedToken]);

	if (!accessToken) {
		return (
			<>
				<a href={signInPath} id='adage-sign-in-btn-nojs' className='btn-primary cpgms-main-header-utility-signin' >{signInText}</a >
			</>
		);
	}

	return (
		<CicAppContext.Provider value={CicAppKeys.ORG} >
			<CpgAppSelect navigation={navigation} />
			<ExternalLinkModal navigation={navigation} />
			<NotificationBell />
			<SignedInToast navigation={navigation} />
			<SessionTimeout
				onActivity={() => getAccessToken()}
				logout={() => performSiteUserLogout(logout, logoutUrl)}
				logoutUrl={logoutUrl}
				timeout={AuthSetting.REACT_APP_SESSION_TIMEOUT}
				countdown={AuthSetting.REACT_APP_SESSION_COUNTDOWN}
				throttle={AuthSetting.REACT_APP_SESSION_THROTTLE}
			/>
		</CicAppContext.Provider>

	);
};
